export default function InheritedPropertySection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Special Considerations for <strong>Inherited Properties</strong>
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            <strong>Selling inherited property with liens</strong> presents unique challenges. You're dealing with emotional stress, family dynamics, legal complexity, and financial burden all at once. We understand, and we're here to help.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gray-50 rounded-lg p-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
              <i className="ri-emotion-sad-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Emotional Burden</h3>
            <p className="text-gray-700 leading-relaxed">
              Inheriting a house often comes during grief. You're mourning a loss while simultaneously dealing with legal paperwork, family disagreements, and financial decisions. It's overwhelming.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
              <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Financial Pressure</h3>
            <p className="text-gray-700 leading-relaxed">
              You're suddenly responsible for property taxes, insurance, utilities, and maintenance on a house you don't live in. These costs add up quickly, especially if the property has been neglected.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
              <i className="ri-team-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Family Dynamics</h3>
            <p className="text-gray-700 leading-relaxed">
              Multiple heirs often have different opinions about what to do with the property. Some want to sell, others want to keep it. These disagreements can tear families apart.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 md:p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Common Title Issues with Inherited Properties
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Undisclosed Liens</h4>
                  <p className="text-gray-700 leading-relaxed">
                    The deceased may have had debts you didn't know about. Tax liens, judgment liens, mechanic's liens, or Medicaid estate recovery claims can all attach to inherited property.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Probate Delays</h4>
                  <p className="text-gray-700 leading-relaxed">
                    You can't sell until probate is complete and you have legal authority. This can take 6-18 months, during which you're paying carrying costs.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Multiple Heirs</h4>
                  <p className="text-gray-700 leading-relaxed">
                    When multiple people inherit, all must agree to sell. One holdout can prevent the sale, creating a clouded title situation.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Missing Documentation</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Previous transfers may not have been properly recorded. Quitclaim deeds, unrecorded transfers, or missing paperwork create chain of title issues.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Property Condition</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Inherited homes are often in poor condition from years of deferred maintenance. Traditional buyers won't purchase without expensive repairs.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Back Taxes</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Property taxes may be years behind, creating liens that must be paid before the property can be sold with clear title.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/8e06a1a4064d015152f9ca4484f9a266.jpg"
              alt="Compassionate consultation for inherited property"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Heirs Choose to Sell to Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">We Understand Your Situation</p>
                  <p className="text-gray-700">We've worked with hundreds of heirs. We know this is difficult, and we treat you with compassion and respect.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">We Handle All the Complexity</p>
                  <p className="text-gray-700">Liens, probate, multiple heirs, property condition - we deal with all of it so you don't have to.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">We Can Wait for Probate</p>
                  <p className="text-gray-700">We'll give you an offer now and wait for probate to complete before closing. This gives you certainty during uncertainty.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">We Work with All Heirs</p>
                  <p className="text-gray-700">We can structure deals that satisfy multiple heirs with different needs and goals.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">We Buy As-Is</p>
                  <p className="text-gray-700">No repairs, no cleaning, no staging. We buy inherited properties in any condition.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-gray-900">Fast Cash Payment</p>
                  <p className="text-gray-700">Get cash in hand quickly so you can settle the estate and move forward with your life.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-emerald-500 text-white rounded-lg p-8 md:p-12">
          <h3 className="text-3xl font-bold mb-6 text-center">A Message to Heirs Dealing with Title Issues</h3>
          <p className="text-xl leading-relaxed mb-6 max-w-4xl mx-auto">
            We know you didn't ask for this. You inherited a property with problems you didn't create, and now you're responsible for solving them. It's not fair, and it's not easy.
          </p>
          <p className="text-xl leading-relaxed mb-6 max-w-4xl mx-auto">
            But you don't have to face this alone. We've helped hundreds of heirs in El Paso sell properties with title issues. We know <strong>how to fix title defects</strong>, clear liens, navigate probate, and resolve family disputes. We handle all the paperwork, all the negotiations, and all the complications.
          </p>
          <p className="text-xl leading-relaxed mb-8 max-w-4xl mx-auto">
            You can walk away with cash in hand and peace of mind, knowing the property is no longer your burden. Call us today for a free, no-obligation consultation. Let's find a solution together.
          </p>
          <div className="text-center">
            <a 
              href="tel:+19152065814"
              className="inline-block bg-white text-emerald-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              Call (915) 206-5814 Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}